import { MutableRefObject, ReactNode, useEffect, useRef, useState } from 'react';
import { createContext } from 'react';

import { usePreferences } from '../hooks/use-preferences';
import { DashboardTabsKeysEnum } from '../model/dashboard-tabs';

import { GlobalTabsKeysEnum } from './models/custom-app-links';
import { DataParent } from './portfolio-management';
import { riskTypes } from './risk-management/risk-management-provider';

// GlobalTabsKeysEnum is now the single canonical enum in
// providers/models/custom-app-links.ts (IM-2022 nav-model foundation). Re-export
// it here so every existing importer (`from '../global-provider'`) keeps working
// unchanged, while feature trees ported from staging that import it directly from
// custom-app-links resolve against the very same enum. All members the beta
// transplants rely on (pricing / marketProbabilities / optionsFlow) live there.
export { GlobalTabsKeysEnum };

export interface ActiveChannelsProps {
  [compositeKey: string]: number;
}

export type ConnectionErrorVariant = 'network' | 'access';

interface IGlobalParamsProvider {
  children?: ReactNode;
}

interface IGlobalProvider {
  currentTab: GlobalTabsKeysEnum;
  setCurrentTab: (value: any) => void;
  currentTabRef: MutableRefObject<GlobalTabsKeysEnum>;
  marketDataOverviewCurrentTab: string;
  setMarketDataOverviewCurrentTab: (value: any) => void;
  marketDataOverviewCurrentToken: string;
  portfolioCurrentTab: DataParent;
  setPortfolioCurrentTab: (value: any) => void;
  renderSettings: boolean;
  setRenderSettings: (value: boolean) => void;
  activeChannelsRef: React.MutableRefObject<ActiveChannelsProps>;
  params: any;
  isChangeAccount: React.MutableRefObject<boolean>;
  setParams: (tab: string, params: any) => void;
  showReloadMessage: boolean;
  setShowReloadMessage: React.Dispatch<React.SetStateAction<boolean>>;
  connectionErrorVariant: ConnectionErrorVariant;
  setConnectionErrorVariant: React.Dispatch<React.SetStateAction<ConnectionErrorVariant>>;
  messageHandlers: MutableRefObject<Map<any, any>>;
  onUnsubscribeHandlers: MutableRefObject<Map<any, any>>;
}

export const GlobalContext = createContext({} as IGlobalProvider);

export const GlobalProvider = (props: IGlobalParamsProvider) => {
  const { preferences } = usePreferences();
  const preferenceHomePage = preferences?.homepage as GlobalTabsKeysEnum;

  const [currentTab, setCurrentTab] = useState(
    preferenceHomePage && preferenceHomePage.length > 0
      ? preferenceHomePage
      : GlobalTabsKeysEnum.marketData,
  );
  const [renderSettings, setRenderSettings] = useState(false);
  const [marketDataOverviewCurrentTab, setMarketDataOverviewCurrentTab] = useState<string>(
    DashboardTabsKeysEnum.overviewBTC,
  );
  const [portfolioCurrentTab, setPortfolioCurrentTab] = useState<DataParent>(riskTypes.spotLadder);
  const isChangeAccount = useRef<boolean>(false);
  const activeChannelsRef = useRef<ActiveChannelsProps>({});
  const currentTabRef = useRef<GlobalTabsKeysEnum>(GlobalTabsKeysEnum.marketData);
  const messageHandlers = useRef(new Map());
  const onUnsubscribeHandlers = useRef(new Map());
  const [params, dispatchParams] = useState<any>({});
  const [showReloadMessage, setShowReloadMessage] = useState<boolean>(false);
  const [connectionErrorVariant, setConnectionErrorVariant] =
    useState<ConnectionErrorVariant>('network');

  const setParams = (tab: string, params: any) => {
    dispatchParams((prev: any) => ({
      ...prev,
      [tab]: params,
    }));
  };

  const marketDataOverviewCurrentToken = marketDataOverviewCurrentTab.includes('overview_')
    ? marketDataOverviewCurrentTab.replace('overview_', '').toUpperCase()
    : '';

  useEffect(() => {
    currentTabRef.current = currentTab;
  }, [currentTab]);

  useEffect(() => {
    if (preferenceHomePage && preferenceHomePage.length > 0) {
      setCurrentTab(preferenceHomePage);
    }
  }, [preferenceHomePage]);

  return (
    <GlobalContext.Provider
      value={{
        currentTab,
        setCurrentTab,
        currentTabRef,
        marketDataOverviewCurrentTab,
        setMarketDataOverviewCurrentTab,
        marketDataOverviewCurrentToken,
        portfolioCurrentTab,
        setPortfolioCurrentTab,
        renderSettings,
        setRenderSettings,
        activeChannelsRef,
        params,
        isChangeAccount,
        setParams,
        messageHandlers,
        onUnsubscribeHandlers,
        showReloadMessage,
        setShowReloadMessage,
        connectionErrorVariant,
        setConnectionErrorVariant,
      }}
    >
      {props.children}
    </GlobalContext.Provider>
  );
};
